import React from 'react'
import ReactSelect from 'react-select'
import { Box, Text } from 'rebass'
import { Badge } from 'components/Forms/Forms'

const customStyles = (
  theme,
  isMenuOpen,
  hasError,
  isValid,
  disabled,
  isPrefix,
  isSearchable
) => ({
  control: (provided, state) => ({
    ...provided,
    padding: '2px 5px',
    backgroundColor: '#FFFCFA',
    borderColor: 'rgba(1, 42, 89, 0.25)',
    ...(isPrefix
      ? {
          borderTopLeftRadius: 3,
          borderBottomLeftRadius: 3,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }
      : {
          borderRadius: 3,
        }),
    boxShadow: 'none',

    color: '#182D58',

    ...(state.isFocused && {
      boxShadow: '0 0 0 4px #f2f1f4',
      borderColor: '#8093AB',
    }),

    '&:hover': {
      borderColor: '#8093AB',
    },

    ...(hasError && {
      borderColor: '#DB5461',
    }),

    ...(isValid && {
      borderColor: '#558B6E',
    }),
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 4,
    marginBottom: 10,
    marginTop: 10,

    minWidth: 250,

    backgroundColor: '#F9F6F1',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(1, 42, 89, 0.25)',
    borderRadius: 3,

    boxShadow:
      '0px 3px 6px rgba(147, 147, 147, 0.05), 0px 6px 12px rgba(147, 147, 147, 0.1)',
    [`.react-select-option--hide-in-menu`]: {
      display: 'none',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    paddingTop: '10px',
    paddingBottom: '10px',

    backgroundColor: state.isSelected ? 'rgba(1, 42, 89, 0.05)' : 'white',
    fontWeight: state.isSelected ? 'bold' : 'normal',
    color: '#182D58',

    '&:hover': {
      backgroundColor: 'rgba(1, 42, 89, 0.05)',
    },
    ...(disabled && {
      color: 'rgba(1, 43, 91, 0.5)',
    }),
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: 'rgba(1, 42, 89, 0.25)',
    padding: '2px',
    ...(isMenuOpen && {
      color: '#182D58',
      transform: 'rotate(-180deg)',
    }),
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'rgba(1, 42, 89, 0.5)',
    fontSize: '18px',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#182D58',
    [`.react-select-option--hide-in-single`]: {
      display: 'none',
    },
    overflow: 'visible',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 3px',
    paddingRight: 0,
    overflow: 'visible',
    ...(!isSearchable && {
      padding: '9px 3px',
    }),
  }),
})

const Select = ({
  formatOptionLabel,
  badge,
  hasError,
  isValid,
  disabled = false,
  label,
  isPrefix,
  labelColor = '#8094AC',
  isSearchable = true,
  ...props
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const style = React.useMemo(
    () =>
      customStyles(
        {},
        isOpen,
        hasError,
        isValid,
        disabled,
        isPrefix,
        isSearchable
      ),
    [isOpen, hasError, isValid, disabled, isPrefix, isSearchable]
  )

  const handleMenuOpen = React.useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleMenuClose = React.useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
      }}
    >
      {label && (
        <Text
          sx={{
            display: 'block',
            marginBottom: '5px',
            color: labelColor,
            fontSize: '14px',
            fontWeight: 700,
          }}
        >
          {label}
        </Text>
      )}
      <ReactSelect
        styles={style}
        formatOptionLabel={formatOptionLabel}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        isDisabled={disabled}
        isSearchable={isSearchable}
        {...props}
      />
      {badge && <Badge label={badge} />}
    </Box>
  )
}

export default Select
